/**
 * Who hears you when you type: one creature, bot companion or construct (its token is highlighted
 * on the map), a band as a whole (its tokens are), the party (players, allies, bots and constructs),
 * the enemy as a group, a talking encounter, or the players only (out of character). The choice
 * sticks until whoever you picked is out of earshot, or a band you picked is gone. While a Recall
 * Knowledge question is open, the Game Master is one more choice (picked by itself once).
 */
import type { Command, SayTarget } from '../shared/protocol.ts';
import type { CreatureView, PlayerView } from '../shared/view.ts';
import { me, store } from './store.ts';

export interface TalkOption {
  /** 'party', 'players', 'enemies', 'c:<creature id>', 'g:<band id>', 'e:<encounter id>' or 'rk:<recall id>'. */
  value: string;
  label: string;
  /** Whom your words reach (none for a Recall Knowledge question: it isn't speech). */
  target?: SayTarget;
  /** Your open Recall Knowledge question: what you type is asked with askRecall. */
  recallId?: string;
  placeholder: string;
  /** The creature whose token the map highlights. */
  creatureId?: string;
  /** A band's members in view, whose tokens the map highlights. */
  creatureIds?: string[];
  /** Its agent (or Game Master) is composing a reply. */
  thinking?: boolean;
  name?: string;
}

function creatureLabel(c: CreatureView): string {
  if (c.bot) return `💬 ${c.name}`;
  if (c.construct) return `💬 ${c.name} (construct)`;
  const tags: string[] = [];
  if (c.kind) tags.push(c.kind.toLowerCase());
  if (c.ally) tags.push('ally');
  else if (c.surrendered) tags.push('surrendered');
  else if (c.attitude) tags.push(c.attitude);
  else if (c.faction === 'hostile') tags.push('hostile');
  if (c.mind && c.mind !== 'sapient' && !c.construct) tags.push("won't understand");
  return `💬 ${c.name}${tags.length ? ` (${tags.join(', ')})` : ''}`;
}

export function talkOptions(view: PlayerView): TalkOption[] {
  const you = me(view);
  const out: TalkOption[] = [];
  const rk = view.recall;
  if (rk) {
    out.push({
      value: `rk:${rk.id}`, label: `📖 Recall: ${rk.creature}`, recallId: rk.id, thinking: rk.thinking, name: 'The Game Master',
      placeholder: `What do you seek to learn about the ${rk.creature.toLowerCase()}? Ask about one thing… (Enter to ask)`,
    });
  }
  out.push({ value: 'party', label: '🗣 Party', target: { kind: 'party' }, placeholder: 'Say something to the party (your allies hear it too)… (T to type)' });
  if (!you || !view.dungeon) {
    out.push({ value: 'players', label: '🔒 Players only', target: { kind: 'players' }, placeholder: 'Message the other players… (T to type)' });
    return out;
  }
  const near = view.dungeon.creatures
    .filter((c) => c.earshot && (!c.isPc || c.bot) && c.health !== 'dead' && c.detection !== 'hidden')
    .sort((a, b) => Math.max(Math.abs(a.x - you.x), Math.abs(a.y - you.y)) - Math.max(Math.abs(b.x - you.x), Math.abs(b.y - you.y)));
  if (near.some((c) => c.faction === 'hostile')) out.push({ value: 'enemies', label: '⚔ The enemy', target: { kind: 'enemies' }, placeholder: 'Shout to the enemy…' });
  const talk = view.talk;
  for (const c of near) {
    const value = c.encounter ? `e:${c.encounter}` : `c:${c.id}`;
    if (out.some((o) => o.value === value)) continue;
    out.push({
      value, label: creatureLabel(c), creatureId: c.id, name: c.name,
      target: c.encounter ? { kind: 'encounter', id: c.encounter } : { kind: 'creature', id: c.id },
      thinking: c.thinking || (!!c.encounter && talk?.encounterId === c.encounter && talk.thinking),
      placeholder: `Say something to ${c.name}… (Enter to send)`,
    });
  }
  // A band as a whole, next to its last member listed: one of them answers for it (each, when you ask them all).
  // Listed with two or more in view, or while it's your pick and any of it is left.
  for (const b of view.dungeon.bands ?? []) {
    const value = `g:${b.id}`;
    if (!b.earshot || (b.members.length < 2 && store.talkTo !== value)) continue;
    const name = b.name.charAt(0).toUpperCase() + b.name.slice(1);
    const option: TalkOption = {
      value, label: `👥 ${name} (${b.members.length})`, target: { kind: 'group', id: b.id }, creatureIds: b.members, thinking: b.thinking, name,
      placeholder: `Say something to ${b.name}: one of them answers… (Enter to send)`,
    };
    const last = out.reduce((at, o, i) => (o.creatureId && b.members.includes(o.creatureId) ? i : at), -1);
    out.splice(last >= 0 ? last + 1 : out.length, 0, option);
  }
  // A talking door or idol: no creature to show, just the encounter.
  if (talk && !out.some((o) => o.value === `e:${talk.encounterId}`)) {
    out.push({ value: `e:${talk.encounterId}`, label: `💬 ${talk.name}`, target: { kind: 'encounter', id: talk.encounterId }, thinking: talk.thinking, name: talk.name, placeholder: `Say something to ${talk.name}… (Enter to send)` });
  }
  out.push({ value: 'players', label: '🔒 Players only', target: { kind: 'players' }, placeholder: 'Out of character: only the other players see this…' });
  return out;
}

/**
 * Whom you'd talk to right now: your pick if they can still hear you, otherwise an NPC in earshot,
 * otherwise the party. A new Recall Knowledge question becomes your pick (you can pick someone else
 * meanwhile); once it closes, the chat box goes back to whom you picked before it.
 */
export function currentTalk(view: PlayerView, options = talkOptions(view)): TalkOption {
  const recall = options.find((o) => o.recallId);
  if (recall && store.talkRecall !== recall.recallId) {
    store.talkRecall = recall.recallId!;
    if (!store.talkTo?.startsWith('rk:')) store.talkBeforeRecall = store.talkTo;
    store.talkTo = recall.value;
  } else if (!recall && store.talkTo?.startsWith('rk:')) {
    store.talkTo = store.talkBeforeRecall;
    store.talkBeforeRecall = null;
  }
  const picked = store.talkTo ? options.find((o) => o.value === store.talkTo) : undefined;
  if (picked) return picked;
  const natural = view.talk ? options.find((o) => o.value === `e:${view.talk!.encounterId}`) : undefined;
  return natural ?? options.find((o) => !o.recallId) ?? options[0];
}

/** Your words as a Recall Knowledge question, when the chat box is set to ask one (otherwise null: they are speech). */
export function recallQuestion(view: PlayerView | null, text: string): Extract<Command, { type: 'askRecall' }> | null {
  const id = view?.recall?.id;
  return id && currentTalk(view!).recallId === id ? { type: 'askRecall', id, text } : null;
}

/** Picks a creature to talk to (Alt+click, or clicking someone who isn't hostile). */
export function talkToCreature(c: CreatureView): void {
  store.talkTo = c.encounter ? `e:${c.encounter}` : `c:${c.id}`;
}

/** Picks a creature's band to talk to as a whole (Alt+Shift+click); false when it has none. */
export function talkToBand(c: CreatureView): boolean {
  if (!c.band) return false;
  store.talkTo = `g:${c.band}`;
  return true;
}
